import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { MapPin, Globe } from 'lucide-react';
import { useJsonData } from '../../hooks/useJsonData';
import { useSafeAnimation, useSafeVariants, useCanAnimate } from '../ui/SafeMotion';

interface State {
  name: string;
  highlight: boolean;
}

interface PresenceData {
  states: State[];
}

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.4 } },
};

export function Presence() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { data, loading } = useJsonData<PresenceData>('/data/presence.json');
  const canAnimate = useCanAnimate();

  // SSR-safe animation props
  const headerAnim = useSafeAnimation({ opacity: 0, y: 30 }, { opacity: 1, y: 0 }, isInView);
  const badgeAnim = useSafeAnimation(
    { opacity: 0, scale: 0.9 },
    { opacity: 1, scale: 1 },
    isInView
  );
  const footerAnim = useSafeAnimation({ opacity: 0 }, { opacity: 1 }, isInView);
  const variants = useSafeVariants(isInView);

  const states = data?.states || [];

  return (
    <section ref={ref} className="py-20 lg:py-28 bg-eco-green-700 overflow-hidden relative">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      {/* Floating Orbs - only animate on client */}
      {canAnimate ? (
        <>
          <motion.div
            animate={{ y: [0, -20, 0], x: [0, 10, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-20 left-20 w-40 h-40 bg-eco-green-500/30 rounded-full blur-3xl"
          />
          <motion.div
            animate={{ y: [0, 20, 0], x: [0, -10, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute bottom-20 right-20 w-60 h-60 bg-eco-green-600/30 rounded-full blur-3xl"
          />
        </>
      ) : (
        <>
          <div className="absolute top-20 left-20 w-40 h-40 bg-eco-green-500/30 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-60 h-60 bg-eco-green-600/30 rounded-full blur-3xl" />
        </>
      )}

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          initial={headerAnim.initial}
          animate={headerAnim.animate}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <motion.span
            initial={badgeAnim.initial}
            animate={badgeAnim.animate}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 backdrop-blur-sm text-white text-sm font-semibold rounded-full mb-6 border border-white/20"
          >
            <Globe className="w-4 h-4" />
            Our Presence
          </motion.span>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
            Operational Across India
          </h2>
          <p className="text-xl text-eco-green-100 max-w-2xl mx-auto">
            Local execution. Centralized compliance.
          </p>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-wrap justify-center gap-4">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="h-12 w-36 bg-white/10 rounded-full animate-pulse" />
            ))}
          </div>
        )}

        {/* States */}
        {!loading && (
          <motion.div
            variants={containerVariants}
            initial={variants.initial}
            animate={variants.animate}
            className="flex flex-wrap justify-center gap-4"
          >
            {states.map((state) => (
              <motion.div
                key={state.name}
                variants={itemVariants}
                whileHover={{ scale: 1.08, y: -4 }}
                className={`backdrop-blur-sm border rounded-full px-6 py-3 flex items-center gap-2 transition-all cursor-default ${
                  state.highlight
                    ? 'bg-white text-eco-green-700 border-white shadow-lg'
                    : 'bg-white/10 text-white border-white/20 hover:bg-white/20'
                }`}
              >
                <MapPin
                  className={`w-4 h-4 ${state.highlight ? 'text-eco-green-600' : 'text-eco-green-300'}`}
                />
                <span className="font-semibold">{state.name}</span>
                {state.highlight && (
                  <span className="text-xs bg-eco-green-100 text-eco-green-700 px-2 py-0.5 rounded-full ml-1">
                    HQ
                  </span>
                )}
              </motion.div>
            ))}
          </motion.div>
        )}

        <motion.p
          initial={footerAnim.initial}
          animate={footerAnim.animate}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center text-eco-green-200 mt-10 text-sm"
        >
          Headquartered in Ranchi, Jharkhand • Expanding across India
        </motion.p>
      </div>
    </section>
  );
}
